/**
 * AdminDeliveryChains — configurable multi-channel delivery matrix.
 *
 * Per event-type (hearing_reminder, case_disposed, invoice_paid…), an
 * admin picks:
 *   • Which channels to try (multi-select: push / whatsapp / email / sms)
 *   • The order to try them
 *   • Wait time (seconds) before falling back to the next
 *
 * The escalation_engine loop reads these live from `notification_chains`
 * so changes take effect on the next tick — no restart needed.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { GripVertical, X, Save, RefreshCw, Bell, MessageSquare, Mail, Phone, Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";

const CHANNEL_META = {
    push:     { label: "Push",     Icon: Bell,           tone: "text-amber-400 border-amber-400/40" },
    whatsapp: { label: "WhatsApp", Icon: MessageSquare,  tone: "text-emerald-400 border-emerald-400/40" },
    email:    { label: "Email",    Icon: Mail,           tone: "text-sky-400 border-sky-400/40" },
    sms:      { label: "SMS",      Icon: Phone,          tone: "text-indigo-400 border-indigo-400/40" },
};

function ChainEditor({ row, onSave, saving }) {
    const [chain, setChain] = useState(row.channels || []);
    const [enabled, setEnabled] = useState(row.enabled !== false);
    const supported = ["push", "whatsapp", "email", "sms"];

    useEffect(() => { setChain(row.channels || []); setEnabled(row.enabled !== false); }, [row]);

    const dirty = JSON.stringify(chain) !== JSON.stringify(row.channels || [])
        || enabled !== (row.enabled !== false);

    const moveUp = (i) => {
        if (i <= 0) return;
        const next = chain.slice();
        [next[i - 1], next[i]] = [next[i], next[i - 1]];
        setChain(next);
    };
    const moveDown = (i) => {
        if (i >= chain.length - 1) return;
        const next = chain.slice();
        [next[i + 1], next[i]] = [next[i], next[i + 1]];
        setChain(next);
    };
    const remove = (i) => { setChain(chain.filter((_, j) => j !== i)); };
    const add = (ch) => {
        if (chain.some(c => c.channel === ch)) { toast.info(`${ch} already in chain`); return; }
        setChain([...chain, { channel: ch, wait_secs: 300 }]);
    };
    const setWait = (i, secs) => {
        const next = chain.slice();
        next[i] = { ...next[i], wait_secs: Math.max(0, parseInt(secs, 10) || 0) };
        setChain(next);
    };

    return (
        <div className="border border-border p-4" data-testid={`chain-row-${row.event_type}`}>
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="font-mono text-sm">{row.event_type}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                        {row.source === "custom" ? `custom · updated ${row.updated_at?.slice(0, 10) || "—"}` : "using default"}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest cursor-pointer">
                        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)}
                            data-testid={`chain-enabled-${row.event_type}`}/>
                        Enabled
                    </label>
                    <button onClick={() => onSave(row.event_type, { channels: chain, enabled })}
                        disabled={!dirty || saving || chain.length === 0}
                        data-testid={`chain-save-${row.event_type}`}
                        className="text-[10px] uppercase tracking-widest border border-primary bg-primary/10 text-primary hover:bg-primary/20 px-3 py-1.5 disabled:opacity-40 inline-flex items-center gap-1">
                        <Save className="w-3 h-3"/> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>

            <div className="space-y-2 mb-3">
                {chain.length === 0 && (
                    <div className="text-xs text-muted-foreground italic px-2 py-3 border border-dashed border-border">
                        No channels in chain. Add at least one below.
                    </div>
                )}
                {chain.map((c, i) => {
                    const meta = CHANNEL_META[c.channel] || CHANNEL_META.email;
                    const Icon = meta.Icon;
                    return (
                        <div key={c.channel} className="flex items-center gap-2 border border-border bg-background p-2"
                            data-testid={`chain-step-${row.event_type}-${c.channel}`}>
                            <div className="flex flex-col">
                                <button onClick={() => moveUp(i)} disabled={i === 0}
                                    data-testid={`chain-up-${row.event_type}-${c.channel}`}
                                    className="text-muted-foreground hover:text-primary disabled:opacity-20 leading-none text-xs">▲</button>
                                <button onClick={() => moveDown(i)} disabled={i === chain.length - 1}
                                    data-testid={`chain-down-${row.event_type}-${c.channel}`}
                                    className="text-muted-foreground hover:text-primary disabled:opacity-20 leading-none text-xs">▼</button>
                            </div>
                            <span className="text-muted-foreground font-mono text-[10px] w-4">{i + 1}.</span>
                            <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border rounded ${meta.tone}`}>
                                <Icon className="w-3.5 h-3.5"/> {meta.label}
                            </span>
                            <div className="flex-1"/>
                            {i < chain.length - 1 && (
                                <label className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                                    then wait
                                    <input type="number" min={0} max={86400} value={c.wait_secs}
                                        onChange={e => setWait(i, e.target.value)}
                                        data-testid={`chain-wait-${row.event_type}-${c.channel}`}
                                        className="w-16 bg-background border border-border focus:border-primary outline-none px-2 py-1 text-xs font-mono text-right"/>
                                    sec
                                </label>
                            )}
                            {i === chain.length - 1 && (
                                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">terminal</span>
                            )}
                            <button onClick={() => remove(i)}
                                data-testid={`chain-remove-${row.event_type}-${c.channel}`}
                                className="text-muted-foreground hover:text-rose-400 ml-1">
                                <X className="w-3.5 h-3.5"/>
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className="flex flex-wrap gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-2 self-center">Add channel:</span>
                {supported.filter(ch => !chain.some(c => c.channel === ch)).map(ch => {
                    const meta = CHANNEL_META[ch];
                    const Icon = meta.Icon;
                    return (
                        <button key={ch} onClick={() => add(ch)}
                            data-testid={`chain-add-${row.event_type}-${ch}`}
                            className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border px-2 py-1 hover:bg-background/60 ${meta.tone}`}>
                            <Plus className="w-3 h-3"/> <Icon className="w-3 h-3"/> {meta.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default function AdminDeliveryChains() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);
    const [newEventType, setNewEventType] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/notification-chains");
            setRows(data.rows || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (event_type, body) => {
        setSavingKey(event_type);
        try {
            await api.put(`/admin/notification-chains/${event_type}`, body);
            toast.success(`Saved ${event_type}`);
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSavingKey(null); }
    };

    const addNew = () => {
        const k = newEventType.trim().toLowerCase().replace(/\s+/g, "_");
        if (!k) { toast.error("Enter an event type"); return; }
        if (rows.some(r => r.event_type === k)) { toast.info("Already exists — scroll down"); return; }
        setRows([{ event_type: k, channels: [{ channel: "push", wait_secs: 300 }, { channel: "email", wait_secs: 0 }], enabled: true, source: "custom" }, ...rows]);
        setNewEventType("");
    };

    return (
        <>
            <PageHeader title="Delivery Chains"
                subtitle="Per event, pick the channels to try and how long to wait before falling back. Push notifications sometimes never reach a closed iOS Safari or a device with no internet — the escalation loop then fires WhatsApp / Email / SMS on schedule so users never miss a hearing alert."
                action={
                    <button onClick={load} data-testid="chains-refresh"
                        className="inline-flex items-center gap-2 text-xs border border-border px-3 py-2 hover:border-primary transition">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/> Refresh
                    </button>
                }/>

            <div className="flex gap-2 mb-6">
                <input value={newEventType} onChange={e => setNewEventType(e.target.value)}
                    data-testid="chain-new-event-type"
                    placeholder="Add custom event_type (e.g., ecourts_error)"
                    className="flex-1 bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
                <button onClick={addNew} data-testid="chain-new-add"
                    className="text-[10px] uppercase tracking-widest border border-border hover:border-primary px-3 py-2 inline-flex items-center gap-1">
                    <Plus className="w-3 h-3"/> Add event
                </button>
            </div>

            {loading && rows.length === 0 ? (
                <div className="text-sm text-muted-foreground p-8 text-center">Loading…</div>
            ) : (
                <div className="space-y-3">
                    {rows.map(r => (
                        <ChainEditor key={r.event_type} row={r} onSave={save} saving={savingKey === r.event_type}/>
                    ))}
                </div>
            )}
        </>
    );
}
